import logger from './logger';
// Team management utilities
import { Firestore, collection, doc, getDoc, setDoc, addDoc, query, where, getDocs, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';

export interface Team {
  id: string;
  name: string;
  adminUserId: string;
  adminName: string;
  type: 'team' | 'organization';
  status: 'active' | 'inactive';
  subscriptionTier: string;
  memberCount: number;
  settings: {
    allowSelfJoin: boolean;
    requireApproval: boolean;
    isDefault?: boolean;
  };
  createdDate: Date;
  createdBy: string;
}

export interface TeamMember {
  id: string;
  teamId: string;
  userId: string;
  userEmail: string;
  role: 'owner' | 'admin' | 'member';
  joinedAt: Date;
  addedBy: string;
  isActive: boolean;
}

const DEFAULT_TEAM_NAME = "JuggleVision FC";
const DEFAULT_ADMIN_UID = "system"; // System-assigned team

export async function ensureDefaultTeamExists(db: Firestore): Promise<string> {
  try {
    // Check if JuggleVision FC team already exists
    const teamsRef = collection(db, 'groups');
    const q = query(teamsRef, where('name', '==', DEFAULT_TEAM_NAME));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const existingTeam = querySnapshot.docs[0];
      logger.log('✅ Default team already exists:', existingTeam.id);
      return existingTeam.id;
    }
    
    // Create JuggleVision FC default team
    logger.log('🆕 Creating default team:', DEFAULT_TEAM_NAME);
    const defaultTeamData = {
      name: DEFAULT_TEAM_NAME,
      adminUserId: DEFAULT_ADMIN_UID,
      adminName: "JuggleVision System",
      type: "team",
      status: "active",
      subscriptionTier: "free", // Default team starts as free
      memberCount: 0,
      settings: {
        allowSelfJoin: true,
        requireApproval: false,
        isDefault: true // Mark as default team
      },
      createdDate: new Date(),
      createdBy: DEFAULT_ADMIN_UID
    };
    
    const docRef = await addDoc(teamsRef, defaultTeamData);
    logger.log('✅ Default team created with ID:', docRef.id);
    return docRef.id;
    
  } catch (error) {
    logger.error('❌ Error ensuring default team exists:', error);
    throw error;
  }
}

export async function addUserToTeam(
  db: Firestore, 
  teamId: string, 
  userId: string, 
  userEmail: string, 
  role: 'owner' | 'admin' | 'member' = 'member',
  addedBy: string = 'system'
): Promise<void> {
  try {
    // Check if user is already in this team
    const memberRef = doc(db, 'teamMembers', `${teamId}__${userId}`);
    const memberDoc = await getDoc(memberRef);
    
    if (memberDoc.exists() && memberDoc.data().isActive) {
      logger.log('ℹ️ User already in team:', { teamId, userId });
      return;
    }
    
    // Add user to team
    const memberData: Omit<TeamMember, 'id'> = {
      teamId,
      userId,
      userEmail,
      role,
      joinedAt: new Date(),
      addedBy,
      isActive: true
    };
    
    await setDoc(memberRef, memberData);
    
    // Update team document with memberIds array and member count
    try {
      // First try 'teams' collection
      let teamRef = doc(db, 'teams', teamId);
      let teamDoc = await getDoc(teamRef);
      
      if (!teamDoc.exists()) {
        // Fallback to 'groups' collection
        teamRef = doc(db, 'groups', teamId);
        teamDoc = await getDoc(teamRef);
      }
      
      if (!teamDoc.exists()) {
        // Try 'organizations' collection
        teamRef = doc(db, 'organizations', teamId);
        teamDoc = await getDoc(teamRef);
      }
      
      if (teamDoc.exists()) {
        const currentCount = teamDoc.data().memberCount || 0;
        const currentMemberIds = teamDoc.data().memberIds || [];
        
        // Add user to memberIds array if not already present
        if (!currentMemberIds.includes(userId)) {
          await updateDoc(teamRef, { 
            memberCount: currentCount + 1,
            memberIds: arrayUnion(userId)
          });
          console.log(`✅ User added to team memberIds array: ${teamId}`);
        } else {
          // Just update count if user already in memberIds
          await updateDoc(teamRef, { memberCount: currentCount + 1 });
          console.log(`ℹ️ User already in memberIds, just updated count: ${teamId}`);
        }
      } else {
        console.warn(`⚠️ Team document not found in any collection: ${teamId}`);
      }
    } catch (teamUpdateError) {
      console.warn('Could not update team document:', teamUpdateError);
      // Don't fail the entire operation if team document update fails
    }
    
    logger.log('✅ User added to team:', { teamId, userId, userEmail });
    
  } catch (error) {
    logger.error('❌ Error adding user to team:', error);
    throw error;
  }
}

export async function removeUserFromTeam(
  db: Firestore,
  teamId: string,
  userId: string
): Promise<void> {
  try {
    console.log(`🔄 Removing user ${userId} from team ${teamId}`);
    
    const memberRef = doc(db, 'teamMembers', `${teamId}__${userId}`);
    
    // Mark as inactive instead of deleting
    await setDoc(memberRef, { 
      isActive: false,
      removedAt: new Date()
    }, { merge: true });
    
    console.log(`✅ Marked teamMembers record as inactive for user ${userId}`);
    
    // Update team document - remove from memberIds array and update count
    try {
      // First try 'teams' collection
      let teamRef = doc(db, 'teams', teamId);
      let teamDoc = await getDoc(teamRef);
      
      if (!teamDoc.exists()) {
        // Fallback to 'groups' collection
        teamRef = doc(db, 'groups', teamId);
        teamDoc = await getDoc(teamRef);
      }
      
      if (!teamDoc.exists()) {
        // Try 'organizations' collection
        teamRef = doc(db, 'organizations', teamId);
        teamDoc = await getDoc(teamRef);
      }
      
      if (teamDoc.exists()) {
        const currentCount = teamDoc.data().memberCount || 0;
        const currentMemberIds = teamDoc.data().memberIds || [];
        
        // Remove user from memberIds array if present
        if (currentMemberIds.includes(userId)) {
          await updateDoc(teamRef, { 
            memberCount: Math.max(0, currentCount - 1),
            memberIds: arrayRemove(userId)
          });
          console.log(`✅ User removed from team memberIds array: ${teamId}`);
        } else {
          // Just update count if user not in memberIds
          await updateDoc(teamRef, { memberCount: Math.max(0, currentCount - 1) });
          console.log(`ℹ️ User not in memberIds, just updated count: ${teamId}`);
        }
      } else {
        console.warn(`⚠️ Team document not found in any collection: ${teamId}`);
      }
    } catch (teamUpdateError) {
      console.warn('Could not update team document:', teamUpdateError);
      // Don't fail the entire operation if team document update fails
    }
    
    logger.log('✅ User removed from team:', { teamId, userId });
    
  } catch (error) {
    logger.error('❌ Error removing user from team:', error);
    throw error;
  }
}

export async function moveUserToTeam(
  db: Firestore,
  userId: string,
  userEmail: string,
  oldTeamId: string,
  newTeamId: string,
  role: 'owner' | 'admin' | 'member' = 'member',
  movedBy: string = 'system'
): Promise<void> {
  try {
    logger.log('🔄 Moving user between teams:', { userId, oldTeamId, newTeamId });
    
    // Remove from old team
    if (oldTeamId) {
      await removeUserFromTeam(db, oldTeamId, userId);
    }
    
    // Add to new team
    await addUserToTeam(db, newTeamId, userId, userEmail, role, movedBy);
    
    logger.log('✅ User successfully moved between teams');
    
  } catch (error) {
    logger.error('❌ Error moving user between teams:', error);
    throw error;
  }
}

export async function getUserCurrentTeam(db: Firestore, userId: string): Promise<string | null> {
  try {
    // Search teams collection for memberIds containing userId
    const teamsRef = collection(db, 'teams');
    const teamsQuery = query(teamsRef, where('memberIds', 'array-contains', userId));
    const teamsSnapshot = await getDocs(teamsQuery);
    
    if (!teamsSnapshot.empty) {
      return teamsSnapshot.docs[0].id;
    }
    
    // Search organizations collection for memberIds containing userId
    const orgsRef = collection(db, 'organizations');
    const orgsQuery = query(orgsRef, where('memberIds', 'array-contains', userId));
    const orgsSnapshot = await getDocs(orgsQuery);
    
    if (!orgsSnapshot.empty) {
      return orgsSnapshot.docs[0].id;
    }
    
    return null;
  } catch (error) {
    logger.error('❌ Error getting user current team:', error);
    return null;
  }
}